import { NextImage } from '@zurich-framework/nextapp/components';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useTranslation } from '@zurich/common/hooks';
import { useDeleteDialog } from '@zurich/portal/hooks';
import { Stack, Typography } from '@zurich/ui/atoms';
import {
  CircularProgressContainer,
  IconButton,
  Seperator,
} from '@zurich/ui/legacy/components';
import { styled } from '@zurich/ui/legacy/utils';
import { LogoLoaderWithBackdrop } from '@zurich/ui/molecules';
import { useDeleteVSGAPhoto } from '~/hooks/api';
import {
  damagesFieldSelector,
  editDamageId,
  showDamageContentAtom,
} from '~/state';

export type DrawerDamageProps = {
  index: number;
  damageId: string | undefined;
  typeOfDamage: string;
  areaOfDamage: string;
  lastDamage: boolean;
  imageURL?: string;
};

export function DrawerDamage({
  index,
  damageId,
  typeOfDamage,
  areaOfDamage,
  lastDamage,
  imageURL,
}: DrawerDamageProps) {
  const { t } = useTranslation();
  const { fetch: deletePhoto, dataState: deleteDataState } =
    useDeleteVSGAPhoto();

  const [damages, setDamages] = useRecoilState(damagesFieldSelector);

  const setShowDamageContent = useSetRecoilState(showDamageContentAtom);
  const setEditId = useSetRecoilState(editDamageId);
  const setDeleteDialog = useDeleteDialog({
    type: '',
    gender: 'neutral',
  });

  const handleEdit = (damageId: string | undefined) => {
    setShowDamageContent(true);
    setEditId(damageId);
  };

  const handleDelete = () => {
    setDeleteDialog({
      onContinue: () => {
        void deletePhoto({
          params: {
            vars: {
              documentId:
                damages.find((damage) => damage.id === damageId)?.documentId ??
                '',
            },
          },
        }).then(() => {
          setDamages((prev) => prev.filter((damage) => damage.id !== damageId));
        });
      },
      title: t('vsga:general.deleteTitle'),
      description: t('vsga:general.deleteBody'),
    });
  };

  return (
    <>
      <LogoLoaderWithBackdrop
        title={t('photoLoadingTitle')}
        open={deleteDataState === 'loading'}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Typography
          color="darkBlue"
          variant="body1 regular"
        >{`${index + 1}. Schaden`}</Typography>
        <Stack direction="row" columnGap={1}>
          <IconButton
            onClick={() => {
              handleEdit(damageId);
            }}
            size="small"
            variant="pencil"
            color="primary"
          />
          <IconButton
            onClick={handleDelete}
            size="small"
            variant="bin"
            color="error"
            style={{ paddingRight: 0 }}
          />
        </Stack>
      </Stack>

      <DivStyled>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <div>
            <DivStyled>
              <Stack>
                <Typography variant="body2 light">
                  {t('vsga:fields.damageType.label')}
                </Typography>
                <Typography color="darkBlue" variant="body2 regular">
                  {typeOfDamage}
                </Typography>
              </Stack>
            </DivStyled>
            <DivStyled>
              <Stack>
                <Typography variant="body2 light">
                  {t('vsga:fields.vehiclePart.label')}
                </Typography>
                <Typography color="darkBlue" variant="body2 regular">
                  {areaOfDamage}
                </Typography>
              </Stack>
            </DivStyled>
          </div>
          <SpanStyled>
            {imageURL ? (
              <NextImage
                src={imageURL}
                width={40}
                height={40}
                alt={t('altProp.uploadedPhoto')}
              />
            ) : (
              <CircularProgressContainer />
            )}
          </SpanStyled>
        </Stack>
      </DivStyled>
      {!lastDamage && <Seperator margin="0" />}
    </>
  );
}

const DivStyled = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 0),
}));

const SpanStyled = styled('span')(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));
